import { zodResolver } from '@hookform/resolvers/zod';
import { HourglassIcon, Loader2Icon, RefreshCcwIcon, RotateCcwIcon, SparklesIcon, UserCogIcon } from 'lucide-react';
import { useState } from 'react';
import { useForm } from 'react-hook-form';
import type { z } from 'zod';
import type {
  AdminAccountTypeChangeDTO,
  AdminQuotaAddExtraDTO,
  AdminUserQuotaDetailDTO,
} from '@shared/schemas/quota';
import {
  adminAccountTypeChangeSchema,
  adminQuotaAddExtraSchema,
  adminQuotaResetSchema,
} from '@shared/schemas/quota';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Field, FieldError, FieldLabel } from '@/components/ui/field';
import { Input } from '@/components/ui/input';
import { RadioGroup, RadioGroupItem } from '@/components/ui/radio-group';
import { Textarea } from '@/components/ui/textarea';
import { cn } from '@/lib/utils';
import { getAuthErrorMessage } from '@/lib/auth-errors';
import { updateDemoExpiration } from '@/lib/auth-client';
import { getQuotaErrorMessage } from '@/features/quota/quota-errors';
import { useQueryClient } from '@tanstack/react-query';
import { quotaQueryKeys } from '@/features/quota/quota-query-keys';
import {
  useAddExtraQuotaMutation,
  useChangeAccountTypeMutation,
  useRecomputeQuotaMutation,
  useResetQuotaMutation,
} from '@/features/quota/useAdminUserQuota';
import { toast } from 'sonner';

type AccountTypeFormInput = z.input<typeof adminAccountTypeChangeSchema>;
type AccountTypeFormOutput = z.output<typeof adminAccountTypeChangeSchema>;

type AdminUserQuotaActionsPanelProps = {
  detail: AdminUserQuotaDetailDTO;
  userId: string;
};

const demoExpirationChoices = [
  { days: 7, label: '1 semaine', value: '1w' },
  { days: 30, label: '1 mois', value: '1m' },
  { days: 90, label: '3 mois', value: '3m' },
  { days: null, label: 'Sans expiration', value: 'none' },
] as const;

type DemoExpirationChoice = (typeof demoExpirationChoices)[number]['value'];

function resolveExpirationIso(choice: DemoExpirationChoice): string | null {
  const option = demoExpirationChoices.find((item) => item.value === choice);
  return option?.days == null
    ? null
    : new Date(Date.now() + option.days * 24 * 60 * 60 * 1000).toISOString();
}

function ExpirationChoicePills({
  disabled,
  onChange,
  value,
}: {
  disabled: boolean;
  onChange: (choice: DemoExpirationChoice) => void;
  value: DemoExpirationChoice;
}) {
  return (
    <div className="flex flex-wrap gap-2" role="group" aria-label="Durée du compte démo">
      {demoExpirationChoices.map((option) => (
        <button
          key={option.value}
          type="button"
          disabled={disabled}
          data-testid={`quota-demo-expiration-${option.value}`}
          onClick={() => onChange(option.value)}
          className={cn(
            'rounded-full border px-4 py-1.5 text-sm font-medium transition-colors',
            value === option.value
              ? 'border-primary bg-primary/10 text-foreground ring-1 ring-primary'
              : 'border-border bg-background text-muted-foreground hover:border-foreground/20 hover:bg-muted/40',
          )}
        >
          {option.label}
        </button>
      ))}
    </div>
  );
}

export function AdminUserQuotaActionsPanel({ detail, userId }: AdminUserQuotaActionsPanelProps) {
  const [extraOpen, setExtraOpen] = useState(false);
  const [resetOpen, setResetOpen] = useState(false);
  const [accountTypeOpen, setAccountTypeOpen] = useState(false);
  const [expirationOpen, setExpirationOpen] = useState(false);
  const [accountTypeExpiration, setAccountTypeExpiration] = useState<DemoExpirationChoice>('1m');
  const [expirationChoice, setExpirationChoice] = useState<DemoExpirationChoice>('1m');
  const [isExpirationSaving, setIsExpirationSaving] = useState(false);
  const queryClient = useQueryClient();
  const invalidateDetail = () => queryClient.invalidateQueries({ queryKey: quotaQueryKeys.detail(userId) });

  const addExtraMutation = useAddExtraQuotaMutation(userId);
  const resetMutation = useResetQuotaMutation(userId);
  const recomputeMutation = useRecomputeQuotaMutation(userId);
  const changeAccountTypeMutation = useChangeAccountTypeMutation(userId);

  const extraForm = useForm<AdminQuotaAddExtraDTO>({
    defaultValues: { reason: '', tokens: 100_000 },
    resolver: zodResolver(adminQuotaAddExtraSchema),
  });

  const resetForm = useForm({
    defaultValues: { reason: '' },
    resolver: zodResolver(adminQuotaResetSchema),
  });

  const accountTypeForm = useForm<AccountTypeFormInput, undefined, AccountTypeFormOutput>({
    defaultValues: {
      accountType: detail.user.accountType,
      reason: '',
    },
    resolver: zodResolver(adminAccountTypeChangeSchema),
  });

  const selectedAccountType = accountTypeForm.watch('accountType');

  async function handleAddExtra(values: AdminQuotaAddExtraDTO) {
    try {
      await addExtraMutation.mutateAsync(values);
      toast.success('Crédit extra ajouté.');
      setExtraOpen(false);
      extraForm.reset({ reason: '', tokens: 100_000 });
    } catch (error) {
      toast.error(getQuotaErrorMessage(error));
    }
  }

  async function handleReset(values: { reason?: string }) {
    try {
      await resetMutation.mutateAsync(values);
      toast.success('Quota réinitialisé.');
      setResetOpen(false);
      resetForm.reset({ reason: '' });
    } catch (error) {
      toast.error(getQuotaErrorMessage(error));
    }
  }

  async function handleRecompute() {
    try {
      await recomputeMutation.mutateAsync();
      toast.success('Quota recalculé depuis l’usage.');
    } catch (error) {
      toast.error(getQuotaErrorMessage(error));
    }
  }

  async function handleAccountTypeChange(values: AccountTypeFormOutput) {
    const payload: AdminAccountTypeChangeDTO = values.accountType === 'demo'
      ? {
          accountType: 'demo',
          demoExpiresAt: resolveExpirationIso(accountTypeExpiration),
          reason: values.reason,
        }
      : {
          accountType: values.accountType,
          reason: values.reason,
        };

    try {
      await changeAccountTypeMutation.mutateAsync(payload);
      toast.success('Type de compte mis à jour.');
      setAccountTypeOpen(false);
    } catch (error) {
      toast.error(getQuotaErrorMessage(error));
    }
  }

  async function handleSaveExpiration() {
    const demoExpiresAt = resolveExpirationIso(expirationChoice);
    setIsExpirationSaving(true);
    const result = await updateDemoExpiration(userId, { demoExpiresAt });
    setIsExpirationSaving(false);

    if (result.error) {
      toast.error(getAuthErrorMessage(result.error));
      return;
    }

    toast.success(demoExpiresAt
      ? 'Expiration du compte démo mise à jour.'
      : 'Compte démo sans date d’expiration.');
    setExpirationOpen(false);
    void invalidateDetail();
  }

  return (
    <>
      <Card className="rounded-[var(--radius-surface-4xl)] border-border/70">
        <CardHeader>
          <CardTitle>Actions administrateur</CardTitle>
          <CardDescription>
            Opérations ponctuelles sur le quota ou le type de compte.
          </CardDescription>
        </CardHeader>
        <CardContent className="flex flex-wrap gap-3">
          {detail.user.accountType === 'demo' ? (
            <Button
              type="button"
              variant="outline"
              data-testid="quota-manage-expiration"
              onClick={() => {
                setExpirationChoice('1m');
                setExpirationOpen(true);
              }}
            >
              <HourglassIcon />
              Gérer l’expiration
            </Button>
          ) : null}
          {detail.user.accountType === 'demo' && detail.config !== null ? (
            <>
              <Button type="button" variant="outline" onClick={() => setExtraOpen(true)}>
                <SparklesIcon />
                Ajouter un crédit extra
              </Button>
              <Button type="button" variant="outline" onClick={() => setResetOpen(true)}>
                <RotateCcwIcon />
                Réinitialiser la période
              </Button>
              <Button
                type="button"
                variant="outline"
                disabled={recomputeMutation.isPending}
                onClick={() => void handleRecompute()}
              >
                {recomputeMutation.isPending ? <Loader2Icon className="animate-spin" /> : <RefreshCcwIcon />}
                Recalculer depuis l’usage
              </Button>
            </>
          ) : null}
          <Button type="button" variant="outline" onClick={() => setAccountTypeOpen(true)}>
            <UserCogIcon />
            Changer le type de compte
          </Button>
        </CardContent>
      </Card>

      <Dialog open={extraOpen} onOpenChange={setExtraOpen}>
        <DialogContent className="sm:max-w-lg">
          <DialogHeader>
            <DialogTitle>Ajouter un crédit extra</DialogTitle>
            <DialogDescription>Ajoute des tokens supplémentaires à la période en cours.</DialogDescription>
          </DialogHeader>
          <form className="space-y-4" onSubmit={extraForm.handleSubmit(handleAddExtra)}>
            <Field>
              <FieldLabel htmlFor="extra-tokens">Tokens supplémentaires</FieldLabel>
              <Input
                id="extra-tokens"
                type="number"
                min={1}
                {...extraForm.register('tokens', { valueAsNumber: true })}
              />
              <FieldError errors={[extraForm.formState.errors.tokens]} />
            </Field>
            <Field>
              <FieldLabel htmlFor="extra-reason">Motif (optionnel)</FieldLabel>
              <Textarea id="extra-reason" rows={3} {...extraForm.register('reason')} />
            </Field>
            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setExtraOpen(false)}>Annuler</Button>
              <Button type="submit" disabled={addExtraMutation.isPending}>
                {addExtraMutation.isPending ? <Loader2Icon className="animate-spin" /> : null}
                Confirmer
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      <Dialog open={resetOpen} onOpenChange={setResetOpen}>
        <DialogContent className="sm:max-w-lg">
          <DialogHeader>
            <DialogTitle>Réinitialiser la période</DialogTitle>
            <DialogDescription>Clôt la période courante et repart de zéro.</DialogDescription>
          </DialogHeader>
          <form className="space-y-4" onSubmit={resetForm.handleSubmit(handleReset)}>
            <Field>
              <FieldLabel htmlFor="reset-reason">Motif (optionnel)</FieldLabel>
              <Textarea id="reset-reason" rows={3} {...resetForm.register('reason')} />
            </Field>
            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setResetOpen(false)}>Annuler</Button>
              <Button type="submit" disabled={resetMutation.isPending}>
                {resetMutation.isPending ? <Loader2Icon className="animate-spin" /> : null}
                Réinitialiser
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      <Dialog open={accountTypeOpen} onOpenChange={setAccountTypeOpen}>
        <DialogContent className="sm:max-w-2xl">
          <DialogHeader>
            <DialogTitle>Changer le type de compte</DialogTitle>
            <DialogDescription>
              Bascule le compte entre Abonné et Démo.
            </DialogDescription>
          </DialogHeader>
          <form className="space-y-5" onSubmit={accountTypeForm.handleSubmit(handleAccountTypeChange)}>
            <Field>
              <FieldLabel>Nouveau type</FieldLabel>
              <RadioGroup
                value={selectedAccountType}
                onValueChange={(value) => {
                  accountTypeForm.setValue('accountType', value as AdminAccountTypeChangeDTO['accountType'], {
                    shouldDirty: true,
                    shouldValidate: true,
                  });
                }}
                className="grid gap-3 md:grid-cols-2"
              >
                <label className="flex cursor-pointer items-start gap-3 rounded-[var(--radius-surface-3xl)] border bg-background p-4">
                  <RadioGroupItem value="demo" id="account-type-demo" className="mt-0.5" />
                  <div>
                    <p className="font-medium text-foreground">Démo</p>
                    <p className="text-sm text-muted-foreground">Accès limité dans le temps, désactivé à expiration.</p>
                  </div>
                </label>
                <label className="flex cursor-pointer items-start gap-3 rounded-[var(--radius-surface-3xl)] border bg-background p-4">
                  <RadioGroupItem value="subscriber" id="account-type-subscriber" className="mt-0.5" />
                  <div>
                    <p className="font-medium text-foreground">Abonné</p>
                    <p className="text-sm text-muted-foreground">Accès standard, sans limite de durée.</p>
                  </div>
                </label>
              </RadioGroup>
            </Field>

            {selectedAccountType === 'demo' ? (
              <Field>
                <FieldLabel>Durée du compte démo</FieldLabel>
                <ExpirationChoicePills
                  disabled={changeAccountTypeMutation.isPending}
                  onChange={setAccountTypeExpiration}
                  value={accountTypeExpiration}
                />
              </Field>
            ) : null}

            <Field>
              <FieldLabel htmlFor="account-type-reason">Motif (optionnel)</FieldLabel>
              <Textarea id="account-type-reason" rows={3} {...accountTypeForm.register('reason')} />
            </Field>

            <FieldError errors={[accountTypeForm.formState.errors.root]} />

            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setAccountTypeOpen(false)}>Annuler</Button>
              <Button type="submit" disabled={changeAccountTypeMutation.isPending}>
                {changeAccountTypeMutation.isPending ? <Loader2Icon className="animate-spin" /> : null}
                Confirmer la bascule
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      <Dialog
        open={expirationOpen}
        onOpenChange={(open) => {
          if (!isExpirationSaving) setExpirationOpen(open);
        }}
      >
        <DialogContent className="max-w-md rounded-3xl">
          <DialogHeader>
            <DialogTitle>Expiration du compte démo</DialogTitle>
            <DialogDescription>
              Choisissez la nouvelle durée d’accès, ou levez l’expiration.
            </DialogDescription>
          </DialogHeader>
          <ExpirationChoicePills
            disabled={isExpirationSaving}
            onChange={setExpirationChoice}
            value={expirationChoice}
          />
          <DialogFooter>
            <Button
              type="button"
              variant="outline"
              disabled={isExpirationSaving}
              onClick={() => setExpirationOpen(false)}
            >
              Annuler
            </Button>
            <Button
              type="button"
              data-testid="quota-demo-expiration-confirm"
              disabled={isExpirationSaving}
              onClick={() => void handleSaveExpiration()}
            >
              {isExpirationSaving ? <Loader2Icon className="animate-spin" /> : null}
              Confirmer
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}
